/**
 * Docked mini-player — TASK 7.2 Steps 1, 4 (AC 0.3).
 *
 * Extracted from the mock's `_mp()` view model (L1142) and its template
 * (L713-728): a fixed dock holding artwork + title/subtitle + ⏮ ⏯ ⏭, with a
 * 4px progress hairline under the card.
 *
 *   <div style="position:fixed;left:0;right:0;bottom:{{ mpBottom }};z-index:44;padding:0 8px 8px">
 *     <div style="max-width:804px;…background:#241B33;…">                 -> .sm-miniplayer
 *       <div onTouchStart=… onTouchEnd=…>                                 -> .sm-miniplayer__swipe
 *         <a href="#/player" aria-label="Open full player">…</a>          -> .sm-miniplayer__link
 *       <button aria-label="Previous track">⏮</button> …
 *     <div …><div style="width:{{ mp.progW }}…"></div></div>              -> .sm-progress--mini
 *
 * ── AC 0.3: DOCKS ABOVE THE NAV, OCCLUDES NOTHING AT 375 px ───────────────
 * `{{ mpBottom }}` is one of the mock's 61 dynamic style bindings (L955:
 * `calc(64px + env(safe-area-inset-bottom))`) and is therefore applied with
 * CSSOM, never a style string. 64px clears the bottom nav, whose own height is
 * `3px border-top + 6px + 44px item + calc(6px + safe-area)` = 59px + safe
 * area — so the dock sits 5px above the nav's top edge at every viewport
 * width, the bar being full-bleed rather than width-dependent. `main` reserves
 * `padding-bottom:210px` (tokens `--sm-main-padding`), which clears the dock's
 * 64px offset plus the ~74px card + hairline with headroom, so content is not
 * occluded either. apps/web/test/miniplayer-dock.test.ts proves both sums from
 * the PARSED CSS (jsdom has no layout — `getBoundingClientRect()` there is
 * always 0x0 and any assertion on it would pass vacuously).
 *
 * The swipe gestures (`mp.touchStart`/`mp.touchEnd`, ±40px -> next/prev) are
 * kept verbatim; they are the same two handlers the full player uses.
 *
 * ── WHY THIS RETURNS A HANDLE AND NOT AN ELEMENT ──────────────────────────
 * It used to be a pure builder, and the shell rebuilt it from scratch on every
 * store tick. `lib/audio-engine.ts` patches `{pos}` on every `timeupdate` —
 * about four times a second — and `Shell.js#syncState` routed each one into
 * `miniPlayer.remove()` + a fresh `MiniPlayer()`. Three consequences, in
 * descending order of severity:
 *
 *   1. KEYBOARD FOCUS WAS THROWN TO `<body>` FOUR TIMES A SECOND. Focus the
 *      dock's pause button, wait one tick, and `document.activeElement` is
 *      `BODY` while the old node is detached. That is a WCAG 2.4.3 (Focus
 *      Order) and 2.4.7 (Focus Visible) failure on the app's primary
 *      persistent control — the dock is on screen for the whole session.
 *   2. The equaliser/progress CSS animation restarted from frame 0 on every
 *      rebuild, so the thing that is supposed to indicate smooth playback
 *      visibly stuttered.
 *   3. ~44 DOM nodes were allocated and discarded four times a second.
 *
 * `views/player/FullPlayer.js` documents precisely this hazard and solves it
 * with an `update()` handle; the mini-player now has the same shape, and
 * `Shell.js` builds once per TRACK rather than once per tick.
 *
 * @module
 */

import { TESTIDS } from '@streamymusic/shared';

import { append, el } from '../../components/primitives/dom.js';
import { Spinner } from '../../components/primitives/Skeleton.js';
import { NEXT_GLYPH, PREV_GLYPH } from './glyphs.js';

/**
 * The mock's `mpBottom` (L955). Exported so the dock geometry test asserts
 * against the SAME literal the runtime assigns.
 */
export const MINIPLAYER_BOTTOM = 'calc(64px + env(safe-area-inset-bottom))';

/**
 * mock L1142/L1144 — swipe threshold in px. Exported because the FULL player's
 * artwork uses the identical gesture (`fp.touchStart`/`fp.touchEnd`), and two
 * copies of the number would be two places to get it wrong.
 */
export const SWIPE_THRESHOLD_PX = 40;

/**
 * @typedef {object} MiniPlayerModel
 * @property {string} title
 * @property {string} subtitle the mock's `mp.sub` (artist)
 * @property {string} initials
 * @property {string} background CSS gradient for the 44px art (mock `mp.bg`)
 * @property {string|undefined} artUrl real cover-art url (`/api/art/:albumId`)
 *   when the album has a real `covers/…` cover, else `undefined` — in which case
 *   the gradient + initials stand alone (Shell derives this via `albumArtUrl`).
 * @property {boolean} playing
 * @property {boolean} buffering
 * @property {number} progress 0..1
 */

/**
 * @typedef {object} MiniPlayerHandlers
 * @property {() => void} onPrevious
 * @property {() => void} onNext
 * @property {() => void} onToggle
 */

/**
 * @typedef {object} MiniPlayerHandle
 * @property {HTMLElement} element the dock, ready to mount
 * @property {(next: MiniPlayerModel) => void} update repaints in place —
 *   allocates no nodes, moves no focus, restarts no animation
 */

/**
 * Builds (does not mount) the mini-player dock.
 *
 * @param {{ model: MiniPlayerModel, handlers: MiniPlayerHandlers }} options
 * @returns {MiniPlayerHandle}
 */
export function MiniPlayer(options) {
  const { handlers } = options;
  let model = options.model;

  // mock L714 — the dynamic `bottom` binding, via CSSOM.
  const dock = el('div', {
    className: 'sm-miniplayer-dock',
    styleProps: { bottom: MINIPLAYER_BOTTOM },
  });

  const card = el('div', { className: 'sm-miniplayer' }); // mock L715

  // ── swipe surface (mock L716)
  let touchStartX = 0;
  const swipe = el('div', {
    className: 'sm-miniplayer__swipe',
    on: {
      touchstart: (event) => {
        const touch = /** @type {TouchEvent} */ (event).touches[0];
        if (touch !== undefined) touchStartX = touch.clientX;
      },
      touchend: (event) => {
        const touch = /** @type {TouchEvent} */ (event).changedTouches[0];
        if (touch === undefined) return;
        const dx = touch.clientX - touchStartX;
        if (dx < -SWIPE_THRESHOLD_PX) handlers.onNext();
        else if (dx > SWIPE_THRESHOLD_PX) handlers.onPrevious();
      },
    },
  });

  // ── artwork + text (mock L717-720)
  const art = el('span', {
    className: 'sm-thumb sm-thumb--44 sm-miniplayer__art',
    styleProps: { background: model.background }, // mock `{{ mp.bg }}`
  });
  const initialsNode = document.createTextNode(model.initials);
  append(art, initialsNode);
  // Built once and attached/detached by `paint()`, so the spinner's animation
  // is not restarted every time `buffering` happens to still be true.
  const bufferScrim = append(
    el('span', { className: 'sm-buffer-scrim' }),
    Spinner({ size: 'mini', label: 'Buffering' }),
  );

  /**
   * The real cover-art layer, laid absolutely over the gradient thumb (geometry
   * is `.sm-cover__art-img` in components.css, and `.sm-miniplayer__art` is a
   * `position:relative;overflow:hidden` container). Managed in place across
   * updates by {@link syncArt} — none when `artUrl` is undefined, so an art-less
   * track keeps the gradient + initials, exactly the album tiles' fallback.
   *
   * @type {HTMLImageElement|null}
   */
  let artImg = null;

  /** Adds / replaces / removes the cover image to match `model.artUrl`. */
  function syncArt() {
    const url = model.artUrl;
    if (url === undefined) {
      if (artImg !== null) {
        artImg.remove();
        artImg = null;
      }
      return;
    }
    if (artImg !== null) {
      if (artImg.getAttribute('src') === url) return; // unchanged — leave it
      artImg.remove();
      artImg = null;
    }
    const img = /** @type {HTMLImageElement} */ (
      el('img', {
        className: 'sm-cover__art-img',
        attrs: { src: url, alt: '', loading: 'lazy', decoding: 'async' },
        // A 404/broken cover removes itself, revealing the gradient + initials
        // underneath — a broken cover never leaves the dock worse than art-less.
        on: {
          error: () => {
            img.remove();
            if (artImg === img) artImg = null;
          },
        },
      })
    );
    artImg = img;
    // Keep a buffering spinner (a later child) painted ON TOP of the cover.
    art.insertBefore(img, bufferScrim.parentNode === art ? bufferScrim : null);
  }

  const titleLine = el('span', { className: 'sm-miniplayer__title', text: model.title });
  const subLine = el('span', { className: 'sm-miniplayer__sub', text: model.subtitle });
  const text = el('span', { className: 'sm-flex-fill' });
  append(text, titleLine, subLine);

  const link = el('a', {
    className: 'sm-miniplayer__link',
    testId: TESTIDS.OPEN_FULL_PLAYER_LINK,
    attrs: { href: '#/player', 'aria-label': 'Open full player' },
  });
  append(link, art, text);
  swipe.append(link);

  // ── transport (mock L722-724)
  const toggleButton = el('button', {
    className: 'sm-btn sm-btn--mp-toggle',
    testId: TESTIDS.PAUSE_BUTTON,
    attrs: { type: 'button' },
    on: { click: () => handlers.onToggle() },
  });
  append(
    card,
    swipe,
    el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-16',
      text: PREV_GLYPH,
      testId: TESTIDS.PREVIOUS_TRACK_BUTTON,
      attrs: { type: 'button', 'aria-label': 'Previous track' },
      on: { click: () => handlers.onPrevious() },
    }),
    toggleButton,
    el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-16',
      text: NEXT_GLYPH,
      testId: TESTIDS.NEXT_TRACK_BUTTON,
      attrs: { type: 'button', 'aria-label': 'Next track' },
      on: { click: () => handlers.onNext() },
    }),
  );

  // ── progress hairline (mock L726) — `{{ mp.progW }}` via CSSOM
  const track = el('div', { className: 'sm-progress sm-progress--mini' });
  const fill = el('div', { className: 'sm-progress__fill sm-progress__fill--mini' });
  track.append(fill);

  append(dock, card, track);

  /** Writes the model onto the existing nodes. Allocates nothing. */
  function paint() {
    art.style.setProperty('background', model.background);
    initialsNode.data = model.initials;
    syncArt();
    if (model.buffering) {
      if (bufferScrim.parentNode === null) art.append(bufferScrim);
    } else {
      bufferScrim.remove();
    }

    titleLine.textContent = model.title;
    subLine.textContent = model.subtitle;

    const toggleLabel = model.playing ? 'Pause' : 'Play';
    toggleButton.textContent = model.playing ? '‖' : '▶';
    toggleButton.setAttribute('aria-label', toggleLabel);

    fill.style.setProperty(
      'width',
      `${Math.min(100, Math.max(0, model.progress * 100)).toFixed(1)}%`,
    );
  }

  paint();

  return {
    element: dock,
    update(next) {
      model = next;
      paint();
    },
  };
}
